import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { DateTime } from "luxon";
import type { DialogProps } from "../misc/DialogProps";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import { Student, studentsProvider } from "../providers/studentsProvider";
import { useRefresh } from "react-admin";
import { clearDunningProcessPostponement } from "../api/backend.api";
import { createNote } from "../api/backoffice.api";
import { getDunningStatusLabel } from "../utils/dunning";

export function ActivateDunningDialog(
  props: DialogProps & { student: Student; dunningDisabled: boolean; dunningPostponedUntil?: DateTime },
) {
  const { student, dunningDisabled, dunningPostponedUntil, onClose, ...dialogProps } = props;
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const isTemporarilyPostponed = !!dunningPostponedUntil && dunningPostponedUntil > DateTime.now();
  const hasProcessPostponement =
    !!student.dunningProcess?.postponedUntil && student.dunningProcess.postponedUntil > DateTime.now();
  const statusLabel = getDunningStatusLabel(dunningDisabled, dunningPostponedUntil);

  const activate = async () => {
    setLoading(true);
    try {
      await studentsProvider.update("students", {
        id: student.id,
        previousData: student,
        data: {
          dunningDisabled: false,
          dunningPostponedUntil: null,
        },
      });
      if (student.dunningProcess?.id && (isTemporarilyPostponed || hasProcessPostponement)) {
        try {
          await clearDunningProcessPostponement(student.dunningProcess.id, student.id);
        } catch (error) {
          // Some roles (e.g. drivingSchoolCustomerService) may not be allowed to patch dunning-process directly.
          // Continue with student-level reactivation so reactivation is still possible.
          console.warn("Failed to clear dunning process postponement during activation", error);
        }
      }
      // Create note, but don't block activation if it fails
      try {
        await createNote({
          studentUid: student.id,
          body: "✅ Mahnwesen aktiviert.",
        });
      } catch (noteError) {
        console.warn("Failed to create activation note, but activation succeeded", noteError);
      }
      showSuccessToast(`Das Mahnwesen für ${student.name} wurde reaktiviert.`);
      refresh();
      onClose();
    } catch (error) {
      showErrorToast(`Fehler beim Reaktivieren des Mahnwesens für ${student.name}.`);
      console.error("Failed to reactivate dunning:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog {...dialogProps} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mahnwesen reaktivieren</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2">Status: {statusLabel}</Typography>
        </Alert>
        <Typography sx={{ mt: 3 }}>
          Das Mahnwesen für diesen Fahrschüler ist aktuell inaktiv. Wenn du es reaktivierst, nimmt AUTOVIO diesen
          Fahrschüler wieder in das Mahnwesen auf.
        </Typography>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant="contained" loading={loading} onClick={activate}>
          Mahnwesen aktivieren
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
